/**
 * RAOC-22 PR-2 — Copied verbatim from
 * `@atlassian/search-page/src/controllers/config/combined-config-adapter.ts`.
 *
 * Internal helper for `useRovoConfig`. Not exported via a package subpath.
 *
 * Adapts the combined-config response shape to the legacy 1P/3P shape that
 * downstream consumers (third-party-products parser, site-metadata helpers)
 * still operate on. Long-term these consumers should be re-written to read
 * the combined-config shape directly (see TODO QS-9614 in search-page).
 */

import type {
	SearchCombinedConfigPayload,
	SearchCombinedFirstPartyProduct,
	SearchCombinedSiteMetadata,
	SearchCombinedSiteProductInfo,
	SearchCombinedThirdPartyProduct as BaseSearchCombinedThirdPartyProduct,
} from '@atlassian/search-client/config';
import type {
	T3pResource,
	TProductDataRegions,
	TProductEdition,
	TSearch3pConfigAPIResponse,
	TSearchConfigAPIResponse,
} from '@atlassian/search-client/fetch-search-config';

/**
 * Search-client's `SearchCombinedThirdPartyProduct` is narrower than the shape
 * the search-page adapter operates on. Forge connectors also carry `lightIcon`,
 * `entityTypes`, and `environmentType` — these arrive on the same wire payload
 * but are not modelled in the shared client today. Long-term these fields
 * should be promoted into search-client; for now extend locally so we can
 * port the adapter behaviour unchanged.
 *
 * Tracked in the RAOC-22 work doc (Follow-ups / open items):
 * https://hello.atlassian.net/wiki/x/lzhZpAE
 */
type SearchCombinedThirdPartyProduct = BaseSearchCombinedThirdPartyProduct & {
	lightIcon?: string | null;
	entityTypes?: string[] | null;
	environmentType?: string | null;
};

const mapProductInfoToDataRegions = (
	productInfo?: SearchCombinedSiteProductInfo[] | null,
): TProductDataRegions | undefined => {
	if (!productInfo || productInfo.length === 0) {
		return undefined;
	}

	return productInfo.reduce<TProductDataRegions>((acc, info) => {
		if (!info?.name || !info.dataRegion) {
			return acc;
		}

		acc[info.name] = Array.isArray(info.dataRegion) ? info.dataRegion : [info.dataRegion];
		return acc;
	}, {});
};

const mapProductEdition = (
	productEdition?: string | null,
	firstPartyProducts?: SearchCombinedFirstPartyProduct[],
): TProductEdition | undefined => {
	if (!productEdition || !firstPartyProducts || firstPartyProducts.length === 0) {
		return undefined;
	}

	return firstPartyProducts.reduce<TProductEdition>((acc, product) => {
		acc[product.name] = productEdition;
		return acc;
	}, {});
};

const mapSiteMetadata = (
	siteMetadata?: SearchCombinedSiteMetadata | null,
	cloudId?: string,
	firstPartyProducts?: SearchCombinedFirstPartyProduct[],
): TSearchConfigAPIResponse['siteMetadata'] => {
	if (!siteMetadata) {
		return [];
	}

	return [
		{
			siteId: cloudId || '',
			siteName: siteMetadata.siteName || '',
			isNounsAvailable: false, // field removed from aggregator; kept to satisfy TSiteMetadata type contract in search-client
			isRovoEnabled: siteMetadata.isRovoEnabled ?? false,
			isJiraIssueIndexed: siteMetadata.isJiraIssueIndexed ?? false,
			productEdition: mapProductEdition(siteMetadata.productEdition, firstPartyProducts),
			productDataRegions: mapProductInfoToDataRegions(siteMetadata.productInfo),
			rovoFeatures: undefined,
		},
	];
};

const mapFirstPartyProductsToResources = (
	firstPartyProducts: SearchCombinedFirstPartyProduct[],
	cloudId?: string,
	siteName?: string,
): TSearchConfigAPIResponse['resources'] => {
	return firstPartyProducts.map((product) => ({
		id: `ari:cloud:${product.name}::site/${cloudId || ''}`,
		baseUrl: '',
		siteName: siteName || '',
	}));
};

const mapThirdPartyProductsToResources = (
	thirdPartyProducts: SearchCombinedThirdPartyProduct[],
): TSearch3pConfigAPIResponse => {
	return thirdPartyProducts.map((product) => ({
		type: product.name,
		outboundAuthUrl: product.outboundAuthUrl || '',
		isUserOAuthed: product.isUserOAuthed,
		providerId: product.providerId || '',
		connectionId: product.connectionId ?? undefined,
		isImpersonationAuthEnabled: product.isImpersonationAuthEnabled ?? undefined,
		// `product.integrationARI` arrives as `string | null` from the
		// aggregator; `T3pResource['integrationId']` is the branded
		// `PlatformIntegrationAri` nominal type. The value is already an
		// ARI string at this point (it originates server-side from the
		// platform integration registry), so this is structurally safe —
		// the cast only exists to satisfy the brand. Single-step casts
		// (`as PlatformIntegrationAri`) don't work because TS rejects
		// `string`-to-branded direct assignment; the `unknown` hop is the
		// idiomatic workaround. Same pattern is used in
		// `third-party-products.ts`. RAOC-22 follow-up: tighten in
		// search-client by exposing a constructor for branded ARIs.
		integrationId: product.integrationARI
			? (product.integrationARI as unknown as T3pResource['integrationId'])
			: undefined,
		workspaceName: product.workspaceName ?? undefined,
		workspaceUrl: product.workspaceUrl ?? undefined,
		datasourceId: product.datasourceId ?? undefined,
		connectorSyncType: product.connectorSyncType ?? undefined,
		connectorCreatedAt: product.connectorCreatedAt ?? undefined,
		lightIcon: product.lightIcon ?? undefined,
		entityTypes: product.entityTypes ?? undefined,
		environmentType: product.environmentType ?? undefined,
	}));
};

export const adaptCombinedConfigToLegacy = (
	configuration: SearchCombinedConfigPayload,
	cloudId?: string,
): { firstPartyConfig: TSearchConfigAPIResponse; thirdPartyConfig: TSearch3pConfigAPIResponse } => {
	const firstPartyProducts = configuration.firstPartyProducts || [];
	const thirdPartyProducts = configuration.thirdPartyProducts || [];
	const siteMetadata = configuration.siteMetadata;

	return {
		firstPartyConfig: {
			resources: mapFirstPartyProductsToResources(
				firstPartyProducts,
				cloudId,
				siteMetadata?.siteName || '',
			),
			intercomHmac: configuration.intercomHmac || '',
			siteMetadata: mapSiteMetadata(siteMetadata, cloudId, firstPartyProducts),
		},
		thirdPartyConfig: mapThirdPartyProductsToResources(thirdPartyProducts),
	};
};
